import i18n from '../../i18n/i18n';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import {
  IAcademicYearDeleteSuccessResponse,
  IAcademicYearListParams,
  IAcademicYearListResponse,
  IAcademicYearPayload,
  ICreateAcademicYearResponse,
  IErrorResponse,
} from '../../interface';
import academicYearService from '../../services/academicYear';

export const updateAcademicYear = createAsyncThunk<
  ICreateAcademicYearResponse,
  { id: string; payload: IAcademicYearPayload },
  { rejectValue: IErrorResponse }
>('academic-year/update', async ({ id, payload }, { rejectWithValue }) => {
  try {
    const response = await academicYearService.updateAcademicYear(id, payload);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      const errorResponse = error.response?.data;
      return rejectWithValue(
        errorResponse || {
          message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')),
        },
      );
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const createAcademicYear = createAsyncThunk<
  ICreateAcademicYearResponse,
  IAcademicYearPayload,
  { rejectValue: IErrorResponse }
>('academic-year/create', async (body, { rejectWithValue }) => {
  try {
    const response = await academicYearService.postAcademicYear(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      const errorResponse = error.response?.data;
      return rejectWithValue(
        errorResponse || {
          message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')),
        },
      );
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const getAcademicYears = createAsyncThunk<
  IAcademicYearListResponse,
  IAcademicYearListParams,
  { rejectValue: IErrorResponse }
>('academic-year/fetch-all', async (params, { rejectWithValue }) => {
  try {
    const response = await academicYearService.fetchAcademicYears(params);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      const errorResponse = error.response?.data;
      return rejectWithValue(
        errorResponse || {
          message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')),
        },
      );
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

/**
 * Soft-delete an academic year.
 *
 * Deletion is unconditional once the year qualifies (the confirmation is handled
 * by a frontend modal), so this is a single call:
 * - On 200: returns IAcademicYearDeleteSuccessResponse in fulfilled.payload.
 * - On error (e.g. 400 hard block — wrong status / active re-enrollment cycle):
 *   rejectWithValue receives a generic { message } object for a toast.
 *
 * NOTE: the shared axios instance (`services/api.tsx`) rejects with
 * `error.response.data` directly (see its response interceptor), so the value
 * caught here is already the parsed response body — NOT an AxiosError.
 */
export const deleteAcademicYear = createAsyncThunk<
  IAcademicYearDeleteSuccessResponse,
  { id: string },
  { rejectValue: IErrorResponse }
>('academic-year/delete', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await academicYearService.deleteAcademicYear(id);
    return response as IAcademicYearDeleteSuccessResponse;
  } catch (err) {
    const body = err as { message?: string };
    return rejectWithValue({
      message: body?.message || 'Failed to delete academic year',
    });
  }
});

const academicYearActions = {
  createAcademicYear,
  getAcademicYears,
  updateAcademicYear,
  deleteAcademicYear,
};

export default academicYearActions;
